import { PayloadAction } from '@reduxjs/toolkit';

import {
  getDesignsBySize,
  getSizeSpecificAreas,
  getVariationSize,
  isSpreadEmpty,
  printsArea,
  withoutAreaArtwork,
  withSizeSpecificAreasFrom,
} from 'editor/src/store/variants/helpers/sizeSpecificAreas';
import { Variant, VariantsState } from 'editor/src/store/variants/types';

import { getCanonicalArea } from 'editor/src/util/reflectDesignData';
import { ReflectContext } from 'editor/src/util/reflectDesignData/getReflectContext';

/**
 * Makes `area` carry its own artwork per size, or one artwork for every size again. A newly size-specific area keeps
 * its artwork on `displayedSize` only: the other sizes start empty, so each is reported until it gets its own.
 * Turned off, each colour's sizes take that colour's `displayedSize` artwork, as a colour may not be linked to the one
 * on the canvas.
 */
const setSizeSpecificAreaReducer = (
  state: VariantsState,
  action: PayloadAction<{
    area: string;
    sizeSpecific: boolean;
    displayedSize: string;
    reflectContext?: ReflectContext;
  }>,
) => {
  const { area, sizeSpecific, displayedSize, reflectContext } = action.payload;
  const areas = getSizeSpecificAreas(state.product);
  if (!sizeSpecific) {
    state.product.sizeSpecificAreas = areas.filter((sizeSpecificArea) => sizeSpecificArea !== area);
    if (reflectContext) {
      const hasArtwork = ({ designData }: Variant) =>
        !!designData?.spreads.some((spread) => getCanonicalArea(spread.name) === area && !isSpreadEmpty(spread));
      state.variationGroups.forEach(({ variationsInfo }) => {
        const sources = [
          ...(getDesignsBySize(variationsInfo, state.product).get(displayedSize) ?? []),
          ...variationsInfo.filter(hasArtwork).map(({ designData }) => designData),
        ];
        if (!sources.some((source) => printsArea(source, area))) {
          return;
        }
        variationsInfo.forEach((variationInfo) => {
          if (variationInfo.designData) {
            variationInfo.designData = withSizeSpecificAreasFrom(
              variationInfo.designData,
              sources,
              [area],
              reflectContext,
            );
          }
        });
      });
    }
    return;
  }
  if (areas.includes(area)) {
    return;
  }

  state.product.sizeSpecificAreas = [...areas, area];
  state.variationGroups.forEach(({ variationsInfo }) =>
    variationsInfo.forEach((variationInfo) => {
      if (
        variationInfo.designData &&
        getVariationSize(variationInfo.variation, state.product)?.value !== displayedSize
      ) {
        variationInfo.designData = withoutAreaArtwork(variationInfo.designData, area);
      }
    }),
  );
};

export default setSizeSpecificAreaReducer;
